import React, { useEffect, useRef } from "react";
import { X, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAgentCanvas } from "@/contexts/AgentCanvasContext";
import DOMPurify from "dompurify";

interface GenerativeCanvasProps {
  htmlContent: string;
  isStreaming?: boolean;
  className?: string;
}

export function GenerativeCanvas({
  htmlContent,
  isStreaming = false,
  className,
}: GenerativeCanvasProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { closeCanvas, actionHandler, terminateCanvas } = useAgentCanvas();
  const lastActionTimeRef = useRef<number>(0);
  const lastActionStringRef = useRef<string>("");
  const [isIframeLoading, setIsIframeLoading] = React.useState(!isStreaming);

  React.useEffect(() => {
    if (!isStreaming) {
      setIsIframeLoading(true);
    }
  }, [isStreaming]);

  // SECURE HTML SANITIZATION
  // Strip all <script> tags, onload handlers, and other dangerous execution vectors
  // We explicitly allow data-* attributes because our click-interceptor depends on them
  // We allow safe href links (DOMPurify default protocol check strips javascript:)
  const safeHtmlContent = DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: [
      "div",
      "span",
      "p",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "a",
      "button",
      "img",
      "svg",
      "path",
      "g",
      "header",
      "footer",
      "section",
      "article",
      "aside",
      "main",
      "nav",
      "figure",
      "figcaption",
      "strong",
      "b",
      "em",
      "i",
      "br",
      "hr",
      "style",
    ],
    ALLOW_DATA_ATTR: true,
    ADD_ATTR: ["style", "class"],
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
    FORBID_ATTR: [
      "onerror",
      "onload",
      "onclick",
      "onmouseover",
      "onkeydown",
      "onkeyup",
      "onsubmit",
    ],
  });

  const baseTemplate = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <script src="https://cdn.tailwindcss.com"></script>
      <script>
        // Helper function to toggle CSS classes on activation/deactivation
        function toggleClasses(el, activeClassesStr, inactiveClassesStr, activate) {
          if (!activeClassesStr && !inactiveClassesStr) return;
          const activeClasses = (activeClassesStr || '').split(' ').filter(Boolean);
          const inactiveClasses = (inactiveClassesStr || '').split(' ').filter(Boolean);
          if (activate) {
            inactiveClasses.forEach(cls => el.classList.remove(cls));
            activeClasses.forEach(cls => el.classList.add(cls));
          } else {
            activeClasses.forEach(cls => el.classList.remove(cls));
            inactiveClasses.forEach(cls => el.classList.add(cls));
          }
        }

        // Securely intercept physical clicks on interactive elements
        document.addEventListener('click', (e) => {
          // CRITICAL SECURITY: Block synthetic clicks triggered by AI scripts
          if (!e.isTrusted) {
            console.warn('Blocked synthetic click attempt');
            return;
          }

          // Intercept internal anchor links (e.g. for slide presentations) to prevent iframe navigation
          const anchor = e.target.closest('a');
          if (anchor) {
            const href = anchor.getAttribute('href');
            if (href && href.startsWith('#')) {
              e.preventDefault();
              const targetId = href.substring(1);
              const targetElement = document.getElementById(targetId);
              if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
              }
              return;
            }
          }

          const target = e.target.closest('[data-action]');
          if (!target) return;
          
          const action = target.getAttribute('data-action');
          let payload = null;
          try {
            const rawPayload = target.getAttribute('data-payload');
            if (rawPayload) {
              payload = JSON.parse(rawPayload);
            }
          } catch (err) {
            console.error('Invalid payload JSON on button', err);
          }
          
          if (!action || action === 'null' || action === 'undefined') return;

          // ZERO TOKEN COST INTERACTIVITY: switch-tab
          if (action === 'switch-tab') {
            const targetId = target.getAttribute('data-target');
            const tabGroup = target.getAttribute('data-tab-group');
            
            if (tabGroup) {
              const buttons = document.querySelectorAll('[data-action="switch-tab"][data-tab-group="' + tabGroup + '"]');
              buttons.forEach(btn => {
                const isActive = btn === target;
                btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
                const activeClass = btn.getAttribute('data-active-class');
                const inactiveClass = btn.getAttribute('data-inactive-class');
                toggleClasses(btn, activeClass, inactiveClass, isActive);
                
                const panelId = btn.getAttribute('data-target');
                const panel = document.getElementById(panelId);
                if (panel) {
                  if (isActive) {
                    panel.classList.remove('hidden');
                    panel.style.display = '';
                  } else {
                    panel.classList.add('hidden');
                  }
                }
              });
            } else if (targetId) {
              const panel = document.getElementById(targetId);
              if (panel) {
                panel.classList.remove('hidden');
                panel.style.display = '';
              }
            }
            return;
          }

          // ZERO TOKEN COST INTERACTIVITY: toggle-section (accordion)
          if (action === 'toggle-section') {
            const targetId = target.getAttribute('data-target');
            if (targetId) {
              const panel = document.getElementById(targetId);
              if (panel) {
                const isCollapsed = panel.classList.contains('hidden') || panel.style.display === 'none';
                
                if (isCollapsed) {
                  panel.classList.remove('hidden');
                  panel.style.display = '';
                  target.setAttribute('aria-expanded', 'true');
                } else {
                  panel.classList.add('hidden');
                  target.setAttribute('aria-expanded', 'false');
                }

                const activeClass = target.getAttribute('data-active-class');
                const inactiveClass = target.getAttribute('data-inactive-class');
                toggleClasses(target, activeClass, inactiveClass, isCollapsed);

                const arrow = target.querySelector('[data-arrow]') || target.querySelector('.arrow');
                if (arrow) {
                  if (isCollapsed) {
                    arrow.classList.add('rotate-180');
                  } else {
                    arrow.classList.remove('rotate-180');
                  }
                }
              }
            }
            return;
          }

          // Secure communication back to host window using explicit parent origin
          const parentOrigin = "${window.location.origin}";
          window.parent.postMessage({
            type: 'MANAGECLEAN_ACTION',
            action,
            payload
          }, parentOrigin);
        });
      </script>
      <script>
        tailwind.config = {
          theme: {
            extend: {
              colors: {
                brand: {
                  50: '#f0f9ff',
                  100: '#e0f2fe',
                  500: '#0ea5e9',
                  600: '#0284c7',
                  900: '#0c4a6e',
                }
              }
            }
          }
        }
      </script>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          background-color: transparent;
          margin: 0;
          padding: 1rem;
          color: #1f2937;
        }
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        ::-webkit-scrollbar-track {
          background: transparent; 
        }
        ::-webkit-scrollbar-thumb {
          background: #cbd5e1; 
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8; 
        }
      </style>
    </head>
    <body class="antialiased">
      ${safeHtmlContent}
    </body>
    </html>
  `;

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // SECURE ORIGIN VERIFICATION: Only allow messages from the null origin (sandboxed iframe) or same origin
      if (event.origin !== "null" && event.origin !== window.location.origin) {
        console.warn("Blocked message from unauthorized origin:", event.origin);
        return;
      }

      if (
        event.data &&
        event.data.type === "MANAGECLEAN_ACTION" &&
        typeof event.data.action === "string" &&
        event.data.action.trim() !== "" &&
        event.data.action !== "null" &&
        event.data.action !== "undefined"
      ) {
        const now = Date.now();
        const actionString = JSON.stringify({
          action: event.data.action,
          payload: event.data.payload,
        });

        // Prevent duplicate spamming from rapid clicking
        if (now - lastActionTimeRef.current < 1000) return;
        if (actionString === lastActionStringRef.current && now - lastActionTimeRef.current < 5000)
          return;

        lastActionTimeRef.current = now;
        lastActionStringRef.current = actionString;

        if (actionHandler) {
          actionHandler(event.data.action, event.data.payload);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [actionHandler]);

  return (
    <div
      className={cn(
        "relative flex h-full w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm",
        className
      )}
    >
      {/* Canvas Header / Controls */}
      <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded bg-blue-50 text-blue-600">
            <LayoutDashboard className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              {isStreaming ? "Generating Canvas..." : "Interactive Canvas"}
            </h3>
            <p className="text-xs text-gray-500">
              {isStreaming
                ? "The AI is building your visualization"
                : "Generated by ManageClean OS Assistant"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!isStreaming && (
            <Button
              variant="outline"
              size="sm"
              className="flex h-8 items-center gap-1.5 rounded-lg border-red-100 px-3 text-xs font-medium text-red-600 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700"
              onClick={terminateCanvas}
              title="Reset Canvas and Kill Threads"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500"></span>
              </span>
              Kill Process
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            onClick={closeCanvas}
            title="Close Canvas"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Canvas Body */}
      <div className="relative flex-1 overflow-hidden bg-gray-50/50">
        {(isStreaming || isIframeLoading) && (
          /* Gentle Glowing Processing State */
          <div className="absolute inset-0 z-10 flex items-center justify-center p-8 bg-gray-50/50 backdrop-blur-sm">
            <div className="flex min-h-[400px] w-[80%] max-w-4xl flex-col items-center justify-center rounded-2xl border border-blue-100 bg-white shadow-[0_0_60px_-15px_rgba(59,130,246,0.4)] transition-all duration-1000 ease-in-out">
              <div className="flex animate-pulse flex-col items-center gap-5">
                <div className="relative flex h-20 w-20 items-center justify-center">
                  {/* Outer spinning ring */}
                  <div className="absolute inset-0 animate-spin rounded-full border-4 border-blue-100 border-t-blue-500"></div>
                  {/* Inner dashboard icon */}
                  <LayoutDashboard className="h-8 w-8 text-blue-500" />
                </div>
                <div className="space-y-2 text-center">
                  <h3 className="text-xl font-semibold tracking-tight text-gray-800">
                    Building visualization...
                  </h3>
                  <p className="mx-auto max-w-md text-sm text-gray-500">
                    Analyzing data and crafting your interactive canvas.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {!isStreaming && (
          /* Actual Rendered HTML */
          <iframe
            ref={iframeRef}
            srcDoc={baseTemplate}
            className={cn("h-full w-full border-0 transition-opacity duration-300", isIframeLoading ? "opacity-0" : "opacity-100")}
            sandbox="allow-scripts" // Allow scripts but prevent top-level navigation / popups / same-origin access
            referrerPolicy="no-referrer"
            loading="lazy"
            title="Generative UI Canvas"
            onLoad={() => setIsIframeLoading(false)}
          />
        )}
      </div>
    </div>
  );
}
